/** Recommendation engine for the SOL Kids "Match my child" quiz. */
import { COLLECTIONS } from "./collections";

// Bucket → numeric age list
const AGE_RANGE = {
  "2-4": [2, 3, 4],
  "5-7": [5, 6, 7],
  "8-10": [8, 9, 10],
};

const collectionAges = (ageStr) => {
  // e.g. "5-9" -> [5..9]
  const m = String(ageStr).match(/(\d+)\s*-\s*(\d+)/);
  if (!m) return [];
  const a = parseInt(m[1], 10);
  const b = parseInt(m[2], 10);
  const out = [];
  for (let i = a; i <= b; i++) out.push(i);
  return out;
};

// Group colour names → bucket tags
const COLOR_GROUP = {
  yellow: ["sunshine", "lemonade", "honey bee", "spongebob", "citrus"],
  red: ["cherry", "ketchup", "raspberry", "fanta"],
  pink: ["candy floss", "bubble gum", "blossom", "rose"],
  blue: ["sky", "bubbles", "splash", "blueberry", "indigo", "indigo dark", "snorlax", "ice lolly"],
  green: ["minty", "turtle", "kiwi", "sage"],
  purple: ["grape", "violet", "black currant"],
  neutral: ["vanilla", "lily", "ninja", "oreo", "goose", "diet coke", "cherry cola", "peach", "tangerine", "orange juice"],
};

const colorMatch = (collectionColors, target) => {
  const targetSet = new Set(COLOR_GROUP[target] || []);
  return collectionColors.filter((c) => targetSet.has(c.toLowerCase())).length;
};

const PLAY_BOOST = {
  beach: ["ola", "rio", "luna", "viva", "sol"],
  park: ["hola", "bono", "vamos", "tico", "kiko"],
  garden: ["mimi", "bebe", "luna", "nube"],
  city: ["chica", "rosa", "neo", "brio", "zaza"],
};

export function recommend({ age, personality, color, play }) {
  const ages = AGE_RANGE[age] || [];
  const scored = COLLECTIONS.map((c) => {
    let score = 0;

    // Age fit
    const cAges = collectionAges(c.age);
    const ageOverlap = cAges.filter((a) => ages.includes(a)).length;
    if (ageOverlap === 0 && ages.length) score -= 8; // outside age range — soft penalty
    else score += ageOverlap * 2;

    // Personality match
    if (c.personality === personality) score += 6;

    // Colour match
    score += colorMatch(c.colors, color) * 2;

    // Play boost
    if ((PLAY_BOOST[play] || []).includes(c.slug)) score += 4;

    // Slight bonus for featured collections (curated)
    if (c.featured) score += 1;

    return { collection: c, score };
  });
  scored.sort((a, b) => b.score - a.score);
  return {
    primary: scored[0].collection,
    runnersUp: scored.slice(1, 4).map((s) => s.collection),
    debug: scored.slice(0, 4).map((s) => ({ slug: s.collection.slug, score: s.score })),
  };
}

export const QUIZ_QUESTIONS = [
  {
    key: "age",
    title: "How old is your little one?",
    helper: "Pick the closest range — frames adjust easily.",
    options: [
      { value: "2-4", label: "2 – 4 years", emoji: "👶" },
      { value: "5-7", label: "5 – 7 years", emoji: "🧒" },
      { value: "8-10", label: "8 – 10 years", emoji: "👦" },
    ],
  },
  {
    key: "personality",
    title: "Which sounds most like them?",
    helper: "Trust your gut — there's no wrong answer.",
    options: [
      { value: "sunshine-chasers", label: "Sunshine chaser", emoji: "🌞" },
      { value: "non-stop-runners", label: "Always running", emoji: "🏃" },
      { value: "tiny-trendsetters", label: "Tiny trendsetter", emoji: "🎨" },
      { value: "dreamy-wanderers", label: "Quiet daydreamer", emoji: "☁️" },
      { value: "fearless-tryers", label: "Climbs everything", emoji: "🚀" },
      { value: "high-energy-kids", label: "Tornado of energy", emoji: "🤸" },
      { value: "friendly-explorers", label: "Friend of everyone", emoji: "😊" },
    ],
  },
  {
    key: "color",
    title: "Favourite colour these days?",
    helper: "(They'll probably change it tomorrow.)",
    options: [
      { value: "yellow", label: "Sunshine yellow", swatch: "#F4C734" },
      { value: "red", label: "Tomato red", swatch: "#DD2E20" },
      { value: "pink", label: "Bubblegum pink", swatch: "#F0A8C3" },
      { value: "blue", label: "Sky blue", swatch: "#A9CDE5" },
      { value: "green", label: "Lime green", swatch: "#8BC547" },
      { value: "purple", label: "Grape purple", swatch: "#8B6FAE" },
      { value: "neutral", label: "Earthy & neutral", swatch: "#D2C1A3" },
    ],
  },
  {
    key: "play",
    title: "Where do they play most?",
    helper: "Where the magic happens.",
    options: [
      { value: "beach", label: "Beach & water", emoji: "🌊" },
      { value: "park", label: "Park & open fields", emoji: "🌳" },
      { value: "garden", label: "Garden & balconies", emoji: "🌼" },
      { value: "city", label: "Streets, malls, cafés", emoji: "🛍️" },
    ],
  },
];
